import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import PageTopBar from "../../../components/common/PageTopBar";
import { toolbarButtonSx } from "../../../components/common/toolbarStyles";
import { useNotify } from "../../../context/NotificationContext";
import { ApplyLeaveDialog } from "../components/ApplyLeaveDialog";
import { LeaveTable } from "../components/LeaveTable";
import { cancelMyLeave, fetchMyLeave, leaveErrorMessage } from "../services/leaveService";
import { LeaveRequest } from "../types";
import { dateRangeLabel, daysLabel } from "../leaveFormat";

/** My Space → My Leave: the faculty's own requests; apply, and cancel while still allowed. */
export default function MyLeavePage() {
  const notify = useNotify();
  const [rows, setRows] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [toCancel, setToCancel] = useState<LeaveRequest | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchMyLeave()
      .then(setRows)
      .catch(() => setError("Failed to load your leave requests."))
      .finally(() => setLoading(false));
  }, []);

  const pending = rows.filter((r) => r.status === "PENDING").length;
  const approvedDays = useMemo(() => rows.filter((r) => r.status === "APPROVED").reduce((sum, r) => sum + r.days, 0), [rows]);

  const replace = (leave: LeaveRequest) => setRows((prev) => prev.map((r) => (r.id === leave.id ? leave : r)));

  const cancel = async () => {
    if (!toCancel) return;
    setCancelling(true);
    try {
      replace(await cancelMyLeave(toCancel.id));
      notify("Leave cancelled.", "success");
      setToCancel(null);
    } catch (err) {
      notify(leaveErrorMessage(err, "Failed to cancel the leave."), "error");
    } finally {
      setCancelling(false);
    }
  };

  const summary = !loading && rows.length > 0 && (
    <Stack direction="row" spacing={1}>
      {pending > 0 && <Chip size="small" color="warning" label={`${pending} pending`} sx={{ fontWeight: 600 }} />}
      <Chip size="small" variant="outlined" label={`${daysLabel(approvedDays)} approved`} sx={{ fontWeight: 600 }} />
    </Stack>
  );

  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar
        left={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              My Leave
            </Typography>
            {summary}
          </Stack>
        }
        right={
          <Button variant="contained" size="small" sx={toolbarButtonSx} startIcon={<AddIcon />} onClick={() => setApplyOpen(true)}>
            Apply Leave
          </Button>
        }
      />

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <LeaveTable
          rows={rows}
          loading={loading}
          empty={
            <Box>
              <EventBusyOutlinedIcon sx={{ fontSize: 40, color: "grey.400", mb: 1 }} />
              <Typography fontWeight={600}>No leave requests yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Apply for a full day, a range of days or half a day.
              </Typography>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setApplyOpen(true)}>
                Apply Leave
              </Button>
            </Box>
          }
          actions={(leave) =>
            leave.cancellable && (
              <Button size="small" color="error" onClick={() => setToCancel(leave)} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
            )
          }
        />
      )}

      <ApplyLeaveDialog
        open={applyOpen}
        mode="self"
        existing={rows}
        onClose={() => setApplyOpen(false)}
        onApplied={(leave) => {
          setRows((prev) => [leave, ...prev]);
          setApplyOpen(false);
          notify("Leave applied. It's waiting for approval.", "success");
        }}
      />

      <Dialog open={Boolean(toCancel)} onClose={cancelling ? undefined : () => setToCancel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Cancel this leave?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {toCancel && `${dateRangeLabel(toCancel)} · ${daysLabel(toCancel.days)}`}
            {toCancel?.status === "APPROVED" && ". It's already approved; cancelling frees these dates."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToCancel(null)} disabled={cancelling} sx={{ textTransform: "none" }}>
            Keep it
          </Button>
          <Button
            color="error"
            onClick={cancel}
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel leave
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
